/**
 * useHostMutations — Story 2.2: Create / Update / Delete fuer Hosts.
 *
 * Voll typisiert gegen die Welle-2 api-types (POST/PATCH/DELETE /api/v2/hosts).
 * Fehler >= 400 wirft die api-client-Middleware als ApiError — 422-Responses
 * werden im Formular via parseHostValidationError feldgenau angezeigt.
 *
 * Zusaetzlich prueft JEDE mutationFn `result.error` und wirft ihn (wie in
 * useSplitRules): kommt eine Fehlerantwort auf einem Randpfad an der
 * Middleware vorbei (z.B. 401/422 ohne Throw), wuerde die Mutation sonst als
 * Erfolg gelten — der Drawer schlaesse kommentarlos und die Eingaben waeren
 * weg. So bleibt der Drawer offen und zeigt die BE-Meldung.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'
import type { Host } from './useHosts'

export type HostFormData = {
  name: string
  email?: string | null
  phone?: string | null
  solana_wallet_address?: string | null
  notes?: string | null
  currency_preference?: 'HNT' | 'EUR' | 'USD' | null
}

function invalidateHosts(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['hosts'] })
  void queryClient.invalidateQueries({ queryKey: ['host-detail'] })
}

export function useCreateHost() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: HostFormData): Promise<Host | undefined> => {
      const result = await apiClient.POST('/api/v2/hosts', {
        body: { host: data },
      })
      if (result.error) throw result.error
      return result.data?.data
    },
    onSuccess: () => invalidateHosts(queryClient),
  })
}

export function useUpdateHost() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: HostFormData
    }): Promise<Host | undefined> => {
      const result = await apiClient.PATCH('/api/v2/hosts/{id}', {
        params: { path: { id } },
        body: { host: data },
      })
      if (result.error) throw result.error
      return result.data?.data
    },
    onSuccess: () => invalidateHosts(queryClient),
  })
}

export function useDeleteHost() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      const result = await apiClient.DELETE('/api/v2/hosts/{id}', {
        params: { path: { id } },
      })
      if (result.error) throw result.error
    },
    onSuccess: () => invalidateHosts(queryClient),
  })
}
